import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MarketTicker } from "@/components/MarketTicker";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { TrendingUp, Trophy, Activity, Target, ShieldCheck } from "lucide-react";

export default function Performance() {
  const [perf, setPerf] = useState(null);

  useEffect(() => {
    api.get("/performance").then(({ data }) => setPerf(data)).catch(() => {});
  }, []);

  return (
    <div className="pt-16">
      <MarketTicker />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-xs font-semibold text-emerald-300 mb-4">
            <ShieldCheck size={14} /> VERIFIED TRACK RECORD
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Transparent <span className="text-gradient">Performance</span>
          </h1>
          <p className="text-slate-400 mt-3 max-w-2xl mx-auto">
            Every signal tracked, every result logged. This is the real edge behind QuantumSignal AI.
          </p>
        </div>

        {perf && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              <KPI icon={<Trophy />} value={`${perf.win_rate}%`} label="Win Rate" color="text-emerald-400" testid="kpi-win-rate" />
              <KPI icon={<TrendingUp />} value={`+${perf.total_pips.toLocaleString()}`} label="Total Pips" color="text-cyan-400" testid="kpi-total-pips" />
              <KPI icon={<Target />} value={`+${perf.roi_pct}%`} label="6-Month ROI" color="text-amber-400" testid="kpi-roi" />
              <KPI icon={<Activity />} value={perf.total_signals + perf.active_signals} label="Signals Issued" color="text-violet-400" testid="kpi-signals" />
            </div>

            <div className="qs-card p-6 lg:p-8">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-xl font-bold">Account Equity Growth</h2>
                <span className="text-sm text-slate-400">$1,000 → ${perf.equity_curve.at(-1).balance.toLocaleString()}</span>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={perf.equity_curve}>
                  <defs>
                    <linearGradient id="perf" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11 }} interval={4} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} width={56} />
                  <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 8, color: "#F8FAFC" }} />
                  <Area type="monotone" dataKey="balance" stroke="#06B6D4" strokeWidth={2.5} fill="url(#perf)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="grid md:grid-cols-3 gap-5 mt-6">
              <SmallStat value={perf.wins} label="Winning Trades" color="text-emerald-400" />
              <SmallStat value={perf.losses} label="Losing Trades" color="text-red-400" />
              <SmallStat value={perf.active_signals} label="Currently Active" color="text-cyan-400" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const KPI = ({ icon, value, label, color, testid }) => (
  <div className="qs-card p-6" data-testid={testid}>
    <div className={`mb-3 ${color}`}>{React.cloneElement(icon, { size: 24 })}</div>
    <div className={`font-display text-3xl sm:text-4xl font-black font-mono-ticker ${color}`}>{value}</div>
    <div className="text-sm text-slate-500 mt-1">{label}</div>
  </div>
);

const SmallStat = ({ value, label, color }) => (
  <div className="qs-card p-5 text-center">
    <div className={`font-mono-ticker text-2xl font-bold ${color}`}>{value}</div>
    <div className="text-xs text-slate-500 mt-1">{label}</div>
  </div>
);
